import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";

// When SIMSHOW_SSO is enabled, show the "Access via SimShow" panel.
// Otherwise, show the standard MSAL sign-in button.
const SIMSHOW_SSO = import.meta.env.VITE_SIMSHOW_SSO_ENABLED === "true";
// When SSO_LOCAL is also enabled, show a "Sign in (Dev SSO)" button that
// drives the full SSO flow through the backend — works locally and on staging.
const SSO_LOCAL = import.meta.env.VITE_SSO_LOCAL_ENABLED === "true";

const API_BASE = import.meta.env.VITE_API_URL || "http://localhost:8081";

const LoginScreen = () => {
  const { login, isLoading, error } = useAuth();
  const [isAttemptingLogin, setIsAttemptingLogin] = useState(false);

  const handleLogin = async () => {
    setIsAttemptingLogin(true);
    try {
      await login();
    } catch (err) {
      console.error("Login failed:", err);
    } finally {
      setIsAttemptingLogin(false);
    }
  };

  const handleDevSsoLogin = () => {
    // Navigate the full browser window to the backend, which initiates
    // the Microsoft auth code flow and eventually redirects back to /sso-callback.
    window.location.href = `${API_BASE}/auth/dev/oauth-start?environment=DEV`;
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <div className="app-logo">
            <svg
              width="48"
              height="48"
              className="logo-icon"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 2L2 7L12 12L22 7L12 2Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 17L12 22L22 17"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 12L12 17L22 12"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <h1 className="app-title">Sales Bot</h1>
          <p className="app-subtitle">Desktop Meeting Assistant</p>
        </div>

        <div className="login-content">
          {!SIMSHOW_SSO ? (
            <>
              <h2 className="login-title">Welcome Back</h2>
              <p className="login-description">
                Sign in with your Microsoft account to access your meeting bots.
              </p>

              {error && (
                <div className="error-message">
                  <span>{error}</span>
                </div>
              )}

              <button
                onClick={handleLogin}
                disabled={isLoading || isAttemptingLogin}
                className="microsoft-login-btn"
              >
                {isLoading || isAttemptingLogin ? (
                  <div className="loading-content">
                    <div className="spinner"></div>
                    <span>Signing in...</span>
                  </div>
                ) : (
                  <div className="login-btn-content">
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                      <rect x="13" y="1" width="10" height="10" fill="#00a4ef" />
                      <rect x="1" y="13" width="10" height="10" fill="#7fba00" />
                      <rect x="13" y="13" width="10" height="10" fill="#ffb900" />
                    </svg>
                    <span>Sign in with Microsoft</span>
                  </div>
                )}
              </button>
            </>
          ) : SSO_LOCAL ? (
            <>
              <h2 className="login-title">Dev SSO Login</h2>
              <p className="login-description">
                Sign in with your Microsoft account to test the full SimShow
                SSO flow locally.
              </p>

              <button onClick={handleDevSsoLogin} className="microsoft-login-btn">
                <div className="login-btn-content">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <rect x="1" y="1" width="10" height="10" fill="#f25022" />
                    <rect x="13" y="1" width="10" height="10" fill="#00a4ef" />
                    <rect x="1" y="13" width="10" height="10" fill="#7fba00" />
                    <rect x="13" y="13" width="10" height="10" fill="#ffb900" />
                  </svg>
                  <span>Sign in with Microsoft</span>
                </div>
              </button>

              <div className="simshow-info">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shield-icon"
                  style={{ flexShrink: 0 }}
                >
                  <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                  <line x1="12" y1="8" x2="12" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="12" cy="16" r="1" fill="currentColor" />
                </svg>
                <span>Dev SSO mode — <code>VITE_SSO_LOCAL_ENABLED=true</code></span>
              </div>
            </>
          ) : (
            <>
              <h2 className="login-title">Access via SimShow</h2>
              <p className="login-description">
                SalesBot is accessible exclusively through the SimShow portal.
                Please log in to SimShow and open SalesBot from there.
              </p>

              <div className="simshow-info">
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="shield-icon"
                  style={{ flexShrink: 0 }}
                >
                  <path
                    d="M12 22S8 18 8 13V6L12 4L16 6V13C16 18 12 22 12 22Z"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span>Single sign-on is managed by SimShow</span>
              </div>
            </>
          )}
        </div>
      </div>

      <style jsx>{`
        .login-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #f5f3ff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          padding: 20px;
        }

        .login-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
          padding: 40px;
          max-width: 420px;
          width: 100%;
          text-align: center;
          border: 1px solid #e5e7eb;
        }

        .login-header {
          margin-bottom: 40px;
          display: flex;
          flex-direction: column;
          align-items: center;
        }

        .app-logo {
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .logo-icon {
          display: block;
          object-fit: contain;
          color: #e04665;
        }

        .app-title {
          font-size: 28px;
          font-weight: 700;
          color: #1a202c;
          margin: 0 0 8px 0;
        }

        .app-subtitle {
          color: #64748b;
          font-size: 16px;
          margin: 0;
        }

        .login-content {
          margin-bottom: 20px;
        }

        .login-title {
          font-size: 24px;
          font-weight: 600;
          color: #1a202c;
          margin: 0 0 12px 0;
        }

        .login-description {
          color: #64748b;
          font-size: 15px;
          line-height: 1.5;
          margin: 0 0 32px 0;
        }

        .error-message {
          background: #fef2f2;
          border: 1px solid #fecaca;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 20px;
          color: #dc2626;
          font-size: 14px;
        }

        .microsoft-login-btn {
          width: 100%;
          background: #e04665;
          color: white;
          border: none;
          border-radius: 8px;
          padding: 14px 24px;
          font-size: 16px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 8px;
        }

        .microsoft-login-btn:hover:not(:disabled) {
          background: #e63950;
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(224, 71, 101, 0.3);
        }

        .microsoft-login-btn:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .login-btn-content,
        .loading-content {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .simshow-info {
          background: #f8fafc;
          border: 1px solid #e5e7eb;
          border-radius: 8px;
          padding: 14px 16px;
          margin-top: 8px;
          display: flex;
          align-items: center;
          gap: 10px;
          color: #64748b;
          font-size: 13px;
        }

        .shield-icon {
          color: #e04665;
        }
      `}</style>
    </div>
  );
};

export default LoginScreen;
